import { Col, Row } from 'antd';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useIsTeamMember from '../../../../../../hooks/useIsTeamMember';
import { RootState } from '../../../../../../store/rootReducer';
import HSButton from '../../../../../common/Button';
import HSDropdown from '../../../../../common/Dropdown';
import NormalInput from '../../../../../common/NormalInput';
import { KYCDoc } from '../../../../../login/redux/types';
import { InvestorStatus } from '../../../../../login/utils/Status';
import KYCSubmitModal from '../../../../settings/modal/KYCSubmitModal';
import KYCThankYou from '../ThankYou';
import { DocSection, SkipKYC } from './IndividualKYC';

type Props = {
    isSetting?: boolean;
    onClickCancel?: () => void;
};

const InstitutionalKYC: React.FC<Props> = ({ isSetting, onClickCancel }) => {
    const isTeamMember: boolean = useIsTeamMember();

    const constants = useSelector((state: RootState) => state.constants.constants);
    const kycInfo = useSelector((state: RootState) => state.user.investor?.kyc);
    const investorStatus = useSelector((state: RootState) => state.user.investor?.status?.kyc);
    const investor = useSelector((state: RootState) => state.user.investor);
    const HIPL_KYC_status = investor?.kyc?.reviewsV2?.find((i) => i.entity === 'HIPL')?.status;
    const showHIPLBanner =
        investor?.isHIPL &&
        (HIPL_KYC_status === InvestorStatus.PendingKYCAdditionalDetails ||
            HIPL_KYC_status === InvestorStatus.PendingKYCSubmission);

    const disabled =
        isTeamMember ||
        (investorStatus !== InvestorStatus.PendingKYCSubmission &&
            investorStatus !== InvestorStatus.PendingKYCAdditionalDetails &&
            investorStatus !== InvestorStatus.PendingKYCResubmission &&
            !showHIPLBanner);

    const [isCompleted, setCompleted] = useState(false);
    const [formFields, setFormFields] = useState<FormField>({
        entityName: undefined,
        address: undefined,
        address2: undefined,
        country: undefined,
        registerNumber: undefined,
        zipCode: undefined,
        isValidate: false,
    });

    useLayoutEffect(() => {
        if (kycInfo?.info) {
            setFormFields({
                entityName: kycInfo.info.legalName,
                address: kycInfo.info.address?.address1,
                address2: kycInfo.info.address?.address2,
                country: kycInfo.info.address?.country,
                registerNumber: kycInfo.info.identity?.identityNumber,
                zipCode: kycInfo.info.address?.postalCode,
                isValidate: false,
            });
        }
        return () => {
            setFormFields({
                entityName: undefined,
                address: undefined,
                address2: undefined,
                country: undefined,
                registerNumber: undefined,
                zipCode: undefined,
                isValidate: false,
            });
        };
    }, [kycInfo?.info]);

    const form = useMemo(() => {
        const onFieldChange = (data: { name: string; value?: number | string }) => {
            const { name, value } = data;
            let obj = { ...formFields };
            if (name) {
                obj[name] = (value as string) || '';
            }
            setFormFields(obj);
        };

        const countrys: { label: string; value: string }[] = [];
        constants.countries.forEach((c) => {
            countrys.push({
                label: c.name,
                value: c.name,
            });
        });

        return (
            <div>
                <p className="page_title_onboarding my-4">Company Information</p>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Entity name"
                            isRequired
                            value={formFields.entityName}
                            name="entityName"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                        {formFields.isValidate && (!formFields.entityName || formFields.entityName.trim() === '') && (
                            <span className="common_error">Please enter entity name</span>
                        )}
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Company Registration Number"
                            isRequired
                            value={formFields.registerNumber}
                            name="registerNumber"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                        {formFields.isValidate &&
                            (!formFields.registerNumber || formFields.registerNumber.trim() === '') && (
                                <span className="common_error">Please enter company registration number</span>
                            )}
                    </Col>
                </Row>
                <p className="page_title_onboarding my-4 pt-4">Address Information</p>
                <Row gutter={[16, 16]}>
                    <Col xs={24}>
                        <NormalInput
                            title="Address"
                            isRequired
                            value={formFields.address}
                            name="address"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                        {formFields.isValidate && (!formFields.address || formFields.address.trim() === '') && (
                            <span className="common_error">Please enter address</span>
                        )}
                    </Col>
                    <Col xs={24}>
                        <NormalInput
                            title="Address line 2"
                            value={formFields.address2}
                            name="address2"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <HSDropdown
                            label="Country"
                            required
                            value={formFields.country}
                            name="country"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            options={countrys}
                            disabled={disabled}
                        />
                        {formFields.isValidate && (!formFields.country || formFields.country.trim() === '') && (
                            <span className="common_error">Please select country</span>
                        )}
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Zip code"
                            isRequired
                            type="number"
                            value={formFields.zipCode}
                            name="zipCode"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                        {formFields.isValidate && (!formFields.zipCode || formFields.zipCode.trim() === '') && (
                            <span className="common_error">Please enter zip code</span>
                        )}
                    </Col>
                </Row>
            </div>
        );
    }, [formFields, disabled, constants]);

    const isDisable = useMemo(() => {
        const documentCounts: any = {
            'Address Proof': 0,
            'Certificate of Incorporation': 0,
            Constitution: 0,
            'Register of Directors': 0,
            'Register of Shareholders': 0,
            'Financial Statement': 0,
            'Identity Proof': 0,
        };

        if (kycInfo && kycInfo.docs) {
            kycInfo.docs.forEach((d) => {
                if (documentCounts.hasOwnProperty(d.type)) {
                    documentCounts[d.type]++;
                }
            });

            return Object.values(documentCounts).some((count) => count === 0);
        }

        return true;
    }, [kycInfo]);

    return (
        <div>
            {!isCompleted || investorStatus === InvestorStatus.PendingKYCResubmission ? (
                <>
                    {form}
                    <p className="page_title_onboarding my-4 py-4">KYC Documents</p>
                    <Documents disabled={disabled} isSetting={isSetting} />
                    <p className="text-sm italic !font-[Lato]">
                        Please note that all documents are required to be Certified as a True Copy. Acceptable
                        certifiers include notary, lawyers and certified public accountants. The certifying individual
                        is to state his/her name, certifying capacity, license/membership number and date of
                        certification (within the last 3 months).
                    </p>
                    <div
                        className={`flex justify-center items-center mt-4 mb-5 ${isSetting ? 'flex-row' : 'flex-col'}`}
                    >
                        {!disabled && (
                            <KYCSubmitModal
                                isDisable={isDisable}
                                // className="onboarding_btn"
                                onSubmitCallback={() => {
                                    if (!isSetting) {
                                        setCompleted(true);
                                    } else if (onClickCancel) {
                                        onClickCancel();
                                    }
                                }}
                                kycInfo={formFields}
                                setFormFields={setFormFields}
                            />
                        )}
                        {isSetting && onClickCancel && (
                            <HSButton className="onboarding_btn ml-2" light title="Cancel" onClick={onClickCancel} />
                        )}
                        {!isSetting && <SkipKYC disabled={disabled} />}
                    </div>
                </>
            ) : (
                <KYCThankYou />
            )}
        </div>
    );
};

export default InstitutionalKYC;

const Documents = ({ disabled, isSetting }: { disabled: boolean; isSetting?: boolean }) => {
    const kycDocs = useSelector((state: RootState) => state.user.investor?.kyc?.docs);

    let certDocs: KYCDoc[] = [],
        constitutionDocs: KYCDoc[] = [],
        directorsDocs: KYCDoc[] = [],
        shareholdersDocs: KYCDoc[] = [],
        financialDocs: KYCDoc[] = [],
        identityDocs: KYCDoc[] = [],
        addressDocs: KYCDoc[] = [],
        authorisedSignatoryDocs: KYCDoc[] = [];

    if (kycDocs && kycDocs.length > 0) {
        certDocs = kycDocs.filter((k) => k.type === 'Certificate of Incorporation');
        constitutionDocs = kycDocs.filter((k) => k.type === 'Constitution');
        directorsDocs = kycDocs.filter((k) => k.type === 'Register of Directors');
        shareholdersDocs = kycDocs.filter((k) => k.type === 'Register of Shareholders');
        financialDocs = kycDocs.filter((k) => k.type === 'Financial Statement');
        identityDocs = kycDocs.filter((k) => k.type === 'Identity Proof');
        addressDocs = kycDocs.filter((k) => k.type === 'Address Proof');
        authorisedSignatoryDocs = kycDocs.filter((k) => k.type === 'Authorised Signatory List');
    }

    return (
        <div className="mb-4">
            <div>
                <DocSection
                    title="Certificate of Incorporation / Business Registration (Certified True Copy)"
                    docs={certDocs}
                    docType="Certificate of Incorporation"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
                <DocSection
                    title="Constitution / Memorandum and Articles of Association (Certified True Copy)"
                    docs={constitutionDocs}
                    docType="Constitution"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
                <DocSection
                    title="Register of Directors"
                    docs={directorsDocs}
                    docType="Register of Directors"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
                <DocSection
                    title="Register of Shareholders"
                    docs={shareholdersDocs}
                    docType="Register of Shareholders"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
                <DocSection
                    title="Audited Financial Statements"
                    docs={financialDocs}
                    docType="Financial Statement"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
                <DocSection
                    title="NRIC/Passport for all Directors, Authorised Signatories and Ultimate Beneficial Owners (Certified True Copy)"
                    docs={identityDocs}
                    docType="Identity Proof"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
                <DocSection
                    title="Proof of address for all Directors, Authorised Signatories and Ultimate Beneficial Owners"
                    docs={addressDocs}
                    docType="Address Proof"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
                <DocSection
                    title="Authorised Signatory List (Certified True Copy)"
                    docs={authorisedSignatoryDocs}
                    docType="Authorised Signatory List"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
            </div>
        </div>
    );
};

interface FormField {
    [k: string]: string | boolean | undefined;
    entityName?: string;
    registerNumber?: string;
    address?: string;
    address2?: string;
    country?: string;
    zipCode?: string;
    isValidate: boolean;
}
